import {none} from '@danwang/option';
import {useRecordBalanceAnchorMutation} from '@transactions/graphql';
import * as Date from 'common/date';
import * as React from 'react';
import {Button} from 'src/Button';
import DatePicker from 'src/DatePicker/DatePicker';
import InputGroup from 'src/InputGroup';
import AmountInput from 'src/inputs/AmountInput';
import {Modal} from 'src/Modal';
import type {Option} from '@danwang/option';
import type Money from 'common/Money';
import type {DateTime} from 'luxon';
import type {SystemProps} from 'src/Modals';

export type AddSnapshotModalUserProps = {
  accountId: string;
  onSuccess?: () => void;
};

export const AddSnapshotModal = (
  props: AddSnapshotModalUserProps & SystemProps,
) => {
  const formId = React.useId();
  const [recordAnchor, {loading}] = useRecordBalanceAnchorMutation();
  const [date, setDate] = React.useState<DateTime>(Date.today());
  const [value, setValue] = React.useState<Option<Money>>(none);
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (value.isEmpty) {
      setError('Please enter a valid portfolio value');
      return;
    }

    try {
      await recordAnchor({
        variables: {
          accountId: props.accountId,
          date: date.toISODate() ?? '',
          balance: value.get().toInt().amount,
          source: 'manual',
        },
      });
      props.onSuccess?.();
      props.onRequestClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create snapshot');
    }
  };

  const isValid = value.nonEmpty;

  const footer = (
    <>
      <Button
        variant="outline"
        disabled={loading}
        onClick={props.onRequestClose}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        disabled={loading || !isValid}
        type="submit"
        form={formId}
      >
        Add Snapshot
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={props.onRequestClose}
      header="Add portfolio snapshot"
      footer={footer}
    >
      <form id={formId} onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
        <InputGroup label="Date">
          <DatePicker selectedDate={date} onChange={setDate} />
        </InputGroup>
        <InputGroup label="Portfolio value">
          <AmountInput
            value={value}
            onChange={setValue}
            autoFocus
          />
        </InputGroup>
        {error && (
          <div className="text-destructive text-sm">{error}</div>
        )}
      </form>
    </Modal>
  );
};
